import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import LocationSelector from '../components/LocationSelector';
import Tooltip from '../components/Tooltip';

interface LocalProxy {
  username: string;
  password: string;
  country: string;
  location: string;
  duration: number;
  endpoints: {
    http: string;
    socks5: string;
  };
  formats: {
    httpStandard: string;
    httpHostPort: string;
    httpHostPortAt: string;
    httpUserPassHost: string;
    socks5HostPort: string;
  };
}

// Location interface for the selector
interface Location {
  country?: string;
  state?: string;
  city?: string;
}

const ProxiesPage: React.FC = () => {
  const { user } = useAuth();
  const [proxies, setProxies] = useState<LocalProxy[]>([]);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState('httpHostPort');

  // Form state
  const [location, setLocation] = useState<Location>({ country: 'US' });
  const [locationString, setLocationString] = useState('US');
  const [duration, setDuration] = useState(60);
  const [count, setCount] = useState(10);

  // Duration 0 = rotating proxy: every request gets a new IP (backend skips
  // the sticky credential cache for duration 0). Only one proxy string is
  // needed since rotation happens per request, not per string.
  const durations = [
    { value: 0, label: 'Rotating (new IP per request)' },
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '60 minutes' },
    { value: 90, label: '90 minutes' },
    { value: 1440, label: '1 day' },
    { value: 4320, label: '3 days' },
    { value: 10080, label: '7 days' },
  ];

  const isRotating = duration === 0;

  const formatOptions = [
    { value: 'httpHostPort', label: 'hostname:port:username:password' },
    { value: 'httpHostPortAt', label: 'hostname:port@username:password' },
    { value: 'httpUserPassHost', label: 'username:password:hostname:port' },
    { value: 'httpStandard', label: 'username:password@hostname:port' },
  ];

  // Generate proxy endpoint based on environment
  const getProxyEndpoints = () => {
    const isLocalhost = window.location.hostname === 'localhost';
    return {
      http: isLocalhost ? 'localhost:3001' : 'proxy.netloxproxies.com:8080',
      socks5: isLocalhost ? 'localhost:3001' : 'proxy.netloxproxies.com:8080'
    };
  };

  const generateProxies = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    setSuccess(null);

    // Generate proxies locally - no API calls needed!
    const newProxies: LocalProxy[] = [];
    const { http, socks5 } = getProxyEndpoints();

    // Rotating proxies need only ONE string - the IP rotates per request
    const proxyCount = isRotating ? 1 : count;

    for (let i = 0; i < proxyCount; i++) {
      // Generate random string for this proxy
      const randomString = Math.random().toString(36).substring(2, 10).toUpperCase();
      
      // Create branded username following the pattern: {ProxyUserID}_{Location}_{Duration}_{RandomString}
      const brandedUsername = `${user?.proxyUserId}_${locationString}_${duration}_${randomString}`;
      const password = user?.proxyPassword || 'password';

      const proxy: LocalProxy = {
        username: brandedUsername,
        password: password,
        country: location.country || locationString.split('-')[0],
        location: locationString,
        duration,
        endpoints: {
          http,
          socks5
        },
        formats: {
          httpStandard: `${brandedUsername}:${password}@${http}`,
          httpHostPort: `${http}:${brandedUsername}:${password}`,
          httpHostPortAt: `${http}@${brandedUsername}:${password}`,
          httpUserPassHost: `${brandedUsername}:${password}:${http}`,
          socks5HostPort: `${socks5}:${brandedUsername}:${password}`,
        }
      };

      newProxies.push(proxy);
    }

    // Replace existing proxies (auto-clean)
    setProxies(newProxies);
    setSuccess(`Proxies generated successfully!`);
    setGenerating(false);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  // Get all proxy strings for the textarea
  const getProxyList = (): string => {
    return proxies.map(proxy => {
      switch (selectedFormat) {
        case 'httpHostPort': return proxy.formats.httpHostPort;
        case 'httpHostPortAt': return proxy.formats.httpHostPortAt;
        case 'httpUserPassHost': return proxy.formats.httpUserPassHost;
        case 'httpStandard': return proxy.formats.httpStandard;
        default: return proxy.formats.httpHostPort;
      }
    }).join('\n');
  };

  if (!user) return null;

  // Convert bytes to GB (perfect precision)
  const creditsInGB = (Math.floor(user.credits / (1024 * 1024 * 1024) * 100) / 100).toFixed(2);

  const tooltipContent = `This allows you to define the duration for which you wish to maintain a consistent IP address. The adjustable time span ranges from a single minute to a maximum of 7 days (10080 minutes). It's important to note that while we cannot assure the retention of the same IP throughout the entire duration, our advanced system will try to keep it for as long as possible. Choose "Rotating" to get a new IP on every request instead.`;

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Compact Header - Matches TopUp/Settings */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Proxy Management</h1>
            <p className="text-sm text-gray-400">
              Balance: <span className="text-blue-400 font-semibold">{creditsInGB} GB</span>
            </p>
          </div>
          <a
            href="https://t.me/NetloxProxies"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-all text-sm whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
            </svg>
            Help
          </a>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Generation Form */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h2 className="text-base md:text-lg font-semibold text-white mb-4">Generate Proxies</h2>

              {success && (
                <div className="bg-green-600/20 border border-green-500/30 text-green-300 p-2.5 rounded-lg mb-3 text-xs">
                  {success}
                </div>
              )}

              <form onSubmit={generateProxies} className="space-y-3">
                {/* Location Selector */}
                <LocationSelector
                  value={location}
                  onChange={(newLocation, newLocationString) => {
                    setLocation(newLocation);
                    setLocationString(newLocationString);
                  }}
                  disabled={generating}
                />

                <div>
                  <Tooltip content={tooltipContent}>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Duration
                    </label>
                  </Tooltip>
                  <select 
                    value={duration} 
                    onChange={(e) => setDuration(parseInt(e.target.value))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 text-sm"
                    required
                  >
                    {durations.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>

                {/* Count is hidden for rotating proxies - one string is enough
                    since the IP rotates on every request */}
                {isRotating ? (
                  <div className="p-2.5 bg-blue-600/10 border border-blue-500/30 rounded-lg">
                    <div className="text-xs text-blue-300">
                      Rotating proxy: a single proxy string is generated. Every request automatically connects through a new IP.
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Count
                    </label>
                    <input 
                      type="number" 
                      min="1" 
                      max="10000" 
                      value={count}
                      onChange={(e) => setCount(parseInt(e.target.value))}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 text-sm"
                      required
                    />
                  </div>
                )}

                <button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-base"
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Proxies'}
                </button>
              </form>
            </div>
          </div>

          {/* Proxy List */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
                <h2 className="text-base md:text-lg font-semibold text-white">Your Proxies</h2>
                <div className="text-xs text-gray-400">
                  {proxies.length} proxies • Select all with Ctrl+A
                </div>
              </div>
              
              {/* Format Selector */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Proxy Format
                </label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                >
                  {formatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Proxy Support Note */}
              <div className="mb-3 p-2.5 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="text-xs text-gray-300">
                  All proxies support both HTTP/HTTPS and SOCKS5 protocols
                </div>
              </div>
              
              {proxies.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                  </svg>
                  <p className="text-xs md:text-sm">No proxies generated yet. Use the form to create some.</p>
                </div>
              ) : (
                <textarea
                  value={getProxyList()}
                  readOnly
                  className="w-full h-64 md:h-80 bg-gray-900 border border-gray-600 rounded-lg p-3 text-gray-300 font-mono text-xs resize-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                  placeholder="Generated proxies will appear here..."
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProxiesPage;
